<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Sample Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading>
      <span class="float-left mr-2">{{ sample.publicNumber() }}</span>
      <badge-batch :badge_collection="sample_badges" />
      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <button
              v-show="checkPermission('delete sample')"
              title="Delete sample"
              class="btn btn-danger ml-1"
              @click="showDeleteModal"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read sample'">
      <template #gated-content>
        <status-progress
          :current_status="current_status"
          :log_collection="sample_status_logs"
          status_group="sample"
          class="mb-3"
        />
        <context-pills :items="sample_items" class="mt-2 mb-3" />
        <sample-card :sample="sample" />
        <sample-detail-cardstack :sample="sample" />
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BadgeBatch from "@/components/shared/BadgeBatch";
import BasicHeading from "@/components/shared/BasicHeading";
import ContextPills from "@/components/shared/ContextPills";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import SampleCard from "@/components/lab/sample/SampleCard";
import SampleDetailCardstack from "@/components/lab/sample/SampleDetailCardstack";
import StatusProgress from "@/components/shared/StatusProgress";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getModel } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { sampleNotifications } from "@/shared/notifications";
import { notificationOptions } from "@/shared/toast-notification-options";
import SampleExtensionTableDTO from "@/DTO/ExtensionTables/SampleExtensionTableDTO";

Vue.use(VueNotifications, notificationOptions);

const SampleRepository = LabRepositoryFactory.get("sample");

export default {
  name: "SampleDetail",
  components: {
    BadgeBatch,
    BasicHeading,
    ContextPills,
    HeaderButtonGroup,
    PermissionAlertGate,
    SampleCard,
    SampleDetailCardstack,
    StatusProgress,
  },
  mixins: [ToastNotifications],
  data() {
    return {
      loading: true,
      sample: null,
      sample_id: this.$route.params.sample_id || null,
      sample_badges: null,
      sample_items: null,
      sample_status_logs: null,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: "/samples",
            title: "Sample list",
          },
          {
            icon: "fa-pencil",
            linkto: `/samples/${this.sample_id}/edit`,
            title: "Edit sample",
            variant: "warning",
          },
        ],
        dropdown: {
          items: [
          {
              icon: "fa-shovel",
              linkto: `/samples/${this.sample_id}/apply-test-package`,
              text: "Apply test package",
              variant: "info",
            }
          ]
        }
      };
    },
    current_status() {
      return (
        this.sample &&
        this.sample.current_sample_status_log.sample_status.status
      );
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    mergeUpdateFields() {
      const updatedFields = SampleExtensionTableDTO.run([this.sample]);
      if (updatedFields[0]) {
        this.sample.set(updatedFields[0].fields);
      }
    },
    initSample() {
      this.mergeUpdateFields();
      this.sample_badges = [this.sample.receiving_id];
      this.sample_items = [];
      this.sample_status_logs = this.sample.getSampleStatusLogs();

      const submission = this.sample.getSubmission();

      if (submission.id) {
        const project = submission.getProject();
        const sample_type = submission.getSampleType();
        const test_package = submission.getTestPackage();
        const turnaround = submission.getTurnaround();

        this.sample_items = [
          ...this.sample_items,
          {
            to: `/submissions/${submission.id}`,
            faIcon: submission.icon(),
            variant: "dark",
            label: submission.publicNumber(),
          },
        ];

        project.id &&
          (this.sample_items = [
            ...this.sample_items,
            {
              to: `/projects/${project.id}`,
              faIcon: project.icon(),
              variant: "dark",
              label: project.publicNumber(),
            },
          ]);
        sample_type.id &&
          (this.sample_badges = [...this.sample_badges, sample_type.name]);
        test_package.id &&
          (this.sample_badges = [...this.sample_badges, test_package.name]);
        turnaround.id &&
          (this.sample_badges = [...this.sample_badges, turnaround.name]);
      }

      this.sample_badges = [...this.sample_badges, this.sample.identifier];
      this.sample_items = [
        ...this.sample_items,
        {
          to: `/samples/${this.sample_id}/label`,
          faIcon: "tag",
          variant: "dark",
          label: "Print receiving label",
        },
        {
          onClick: () => {
            this.$bvToast.toast("The results have been re-sent.", {
              title: "Results Sent",
              variant: "success",
              autoHideDelay: 2000,
            });
          },
          faIcon: "paper-plane",
          variant: "dark",
          label: "Re-send results to client",
        },
        {
          to: `/samples/${this.sample_id}/results/edit`,
          faIcon: "pencil",
          variant: "dark",
          label: "Manage Results",
        },
        {
          to: `/samples/${this.sample_id}/audit-trail`,
          faIcon: "search",
          variant: "dark",
          label: "Audit Trail",
        },
        // {
        //   to: `/samples/${this.sample_id}/note`,
        //   faIcon: "sticky-note",
        //   variant: "dark",
        //   label: "Add a note",
        // },
      ];
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this sample. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await SampleRepository.suspend(this.sample_id);
  
              this.deleteSuccess();
              this.$router.push("/samples");
            } catch ({ response }) {
              const { status, data } = response.response;
  
              this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
                title: "Server Error.",
                variant: "danger",
              });
            }
          }
        });
    },
  },
  async created() {
    this.loading = true;

    this.sample = await getModel({
      model_type: "sample",
      id: this.sample_id,
      message: this.$bvToast,
    });

    if (this.sample) {
      this.initSample();

      this.loading = false;
    } else {
      this.$router.back();
    }
  },
  notifications: sampleNotifications,
};
</script>
